<template>
  <Modal
    data-testid="portfolio-order-details"
    class="portfolio-order-details"
    size="md"
    :is-open="isOpen"
    @close="$emit('close')"
  >
    <LoaderIndicator v-if="isLoading" size="32" with-container />

    <HelperViewStatus
      v-else-if="hasError"
      class="portfolio-order-details__helper"
      generic-error
      button-label="Tentar novamente"
      @click="fetchOrderDetails"
    />

    <HelperViewStatus
      v-else-if="cancelModalOpen"
      class="portfolio-order-details__helper"
      status="negative"
      title="Cancelar"
      description="Sua solicitação será cancelada e o valor permanecerá no seu produto."
    />

    <div v-else class="portfolio-order-details__content">
      <HeadingDisplay
        size="small"
        :head="orderDetails?.head"
        :title="orderDetails?.title"
      >
        <template #subtitle-component>
          <BaseText size="p5" :color="NebraskaColors.statusInfo">
            {{ orderDetails?.subtitle }}
          </BaseText>
        </template>
      </HeadingDisplay>
      <div class="portfolio-order-details__list">
        <ListDisplay
          v-for="(item, index) in orderDetails?.list"
          :key="index"
          class="portfolio-order-details__list__item"
          :value="item.value"
          :title="item.label"
          :has-divider="index !== (orderDetails?.list?.length ?? 0) - 1"
        />
      </div>
      <div v-if="orderDetails?.message" class="portfolio-order-details__status">
        <div>
          <BaseText :color="NebraskaColors.elementPrimary" size="p3">
            {{ orderDetails.message.title }}
          </BaseText>
          <BaseText size="p6">
            {{ orderDetails.message.description }}
          </BaseText>
        </div>

        <InitialAvatarIcon
          class="portfolio-order-details__avatar"
          :icon="orderDetails.message.icon"
          size="medium"
        />
      </div>
    </div>
    <template v-if="!isLoading && orderDetails?.button" #buttonContainer>
      <ButtonContainer>
        <template #button-primary>
          <ButtonSecondary
            size="large"
            :label="orderDetails.button.label"
            @click="handleClick(orderDetails.button.action)"
          />
        </template>
      </ButtonContainer>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';

import {
  Modal,
  LoaderIndicator,
  HelperViewStatus,
  HeadingDisplay,
  BaseText,
  ListDisplay,
  InitialAvatarIcon,
  ButtonContainer,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import {
  getPortfolioOrderDetails,
  IPortfolioOrderDetails,
  EButtonAction
} from '@/modules/home/services/orders';
import { WealthPortfolioTabs } from '@/modules/wealth/views/WealthPortfolioV2/types';

const router = useRouter();

interface Props {
  isOpen: boolean;
  orderId: string;
  productType: string;
  portfolioType: string;
}
const props = defineProps<Props>();

const orderDetails = ref<IPortfolioOrderDetails | null>(null);
const isLoading = ref(false);
const hasError = ref(false);
const cancelModalOpen = ref(false);

watch(
  () => props.isOpen,
  async isOpen => {
    if (isOpen) {
      fetchOrderDetails();
    }
  }
);

function handleClick(action: EButtonAction) {
  if (action === EButtonAction.OPEN_ORDER) {
    router.push({
      name: 'wealthPortfolio',
      params: {
        id: orderDetails.value!.portfolioId,
        type: props.portfolioType
      },
      query: {
        tab: WealthPortfolioTabs.Orders,
        orderId: props.orderId
      }
    });
  }
}

async function fetchOrderDetails() {
  try {
    isLoading.value = true;
    hasError.value = false;

    orderDetails.value = await getPortfolioOrderDetails(
      props.orderId,
      props.productType
    );
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}
</script>
<style lang="less" scoped>
.portfolio-order-details {
  :deep(.base-modal--wrapper) {
    width: 683px;
    max-width: 683px;
  }

  :deep(.modal-content) {
    text-align: left;
    padding: @size-spacing-x800 @size-spacing-x1200;
  }

  &__helper {
    margin: 0 auto;
    justify-content: center;
  }

  &__list {
    margin-top: @size-spacing-x800;

    &__item {
      margin-bottom: @size-spacing-x400;
      padding-bottom: @size-spacing-x400;
    }
  }

  &__status {
    padding: @size-spacing-x500 @size-spacing-x600;
    background-color: @background-tertiary;
    display: flex;
    justify-content: space-between;
    align-content: center;
    border-radius: @size-radius-x300;
  }

  &__avatar {
    background-color: @status-info-background;
    color: @status-info;
  }
}
</style>
